import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Heart, Loader2, ArrowLeft } from "lucide-react";
import { resolveMedia } from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1765896387398-1e1ae8d2eb85?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { login } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}`);
      navigate("/app");
    } else {
      toast.error(res.error);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Form side */}
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-10 relative paper-grain">
        <Link to="/" className="absolute top-6 left-6 text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
        <div className="max-w-sm w-full mx-auto">
          <div className="flex items-center gap-3 mb-8">
            {brand.logo_url ? (
              <img src={resolveMedia(brand.logo_url)} alt="Love For Mankind" className="h-11 w-auto object-contain" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary-foreground" fill="currentColor" />
              </div>
            )}
            <div className="leading-tight">
              <div className="font-heading font-bold">Love For Mankind</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Operations OS</div>
            </div>
          </div>

          <h1 className="font-heading text-3xl font-bold tracking-tight mb-1">Welcome back</h1>
          <p className="text-muted-foreground text-sm mb-8">Sign in to your operations workspace.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@loveformankindhhc.com" required data-testid="login-email" autoComplete="email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required data-testid="login-password" autoComplete="current-password" />
            </div>
            <Button type="submit" className="w-full rounded-md" disabled={loading} data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-border flex-1" />
            <span className="text-xs text-muted-foreground uppercase tracking-widest">or</span>
            <div className="h-px bg-border flex-1" />
          </div>

          <Button variant="outline" className="w-full rounded-md" onClick={googleLogin} data-testid="google-login-btn">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4 mr-2" />
            Continue with Google
          </Button>
        </div>
      </div>

      {/* Image side */}
      <div className="hidden lg:block relative">
        <img src={resolveMedia(HERO)} alt="Caregiver supporting a client" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-primary/45" />
        <div className="absolute bottom-0 left-0 right-0 p-12 text-primary-foreground">
          <p className="font-heading text-3xl font-bold leading-tight max-w-md">Pursuing Exceptional Care Is Our Passion</p>
          <p className="text-primary-foreground/80 mt-3 max-w-md">One platform for care operations, fleet, referrals, and the people you serve.</p>
        </div>
      </div>
    </div>
  );
}
